import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  visuallyHiddenStyle,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { WarningIcon } from '@udacity/chakra-uds-icons';

import { COPY, STAY_POSTED_EMAIL_INPUT_ID, STAY_POSTED_LEAD_FORM_LABEL } from '~/features/ai-marketer/constants';
import { EnvelopeIcon } from '~/features/ai-marketer/icons';
import { useNewsletterSignUpHandler } from '~/hooks/use-newsletter-signup';

const stayPostedFormSchema = z.object({
  email: z.string().trim().min(1, 'Email is required').email('Please enter a valid email address'),
});

type StayPostedFormData = z.infer<typeof stayPostedFormSchema>;

export function StayPostedForm() {
  const toast = useToast();
  const handleStayPostedSubmit = useNewsletterSignUpHandler({
    formId: STAY_POSTED_LEAD_FORM_LABEL,
    label: STAY_POSTED_LEAD_FORM_LABEL,
    showToast: false,
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<StayPostedFormData>({
    resolver: zodResolver(stayPostedFormSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (data) => {
    await handleStayPostedSubmit(data);
    toast({
      title: COPY.stayPosted.interestToastTitle,
      description: COPY.stayPosted.interestToastDescription,
      variant: 'success',
    });
    reset();
  });

  return (
    <Flex
      as='form'
      flexDirection={{ base: 'column', md: 'row' }}
      gap='8px'
      maxWidth={{ base: '100%', md: '476px' }}
      width='100%'
      noValidate
      onSubmit={onSubmit}
    >
      <FormControl id={STAY_POSTED_EMAIL_INPUT_ID} isInvalid={Boolean(errors.email)}>
        <FormLabel sx={visuallyHiddenStyle}>{COPY.stayPosted.emailLabel}</FormLabel>
        <InputGroup>
          <InputLeftElement height='40px' pointerEvents='none'>
            <EnvelopeIcon color='grey.300' height='18px' width='18px' />
          </InputLeftElement>
          <Input
            _placeholder={{ color: 'gray.300' }}
            autoComplete='email'
            bgColor='blackAlpha.300'
            borderColor='whiteAlpha.400'
            color='white'
            height='40px'
            pl='40px'
            placeholder={COPY.stayPosted.emailPlaceholder}
            type='email'
            {...register('email')}
          />
        </InputGroup>
        {errors.email && (
          <FormErrorMessage fontSize='sm' mt='8px'>
            <WarningIcon h='20px' w='20px' />
            {errors.email.message}
          </FormErrorMessage>
        )}
      </FormControl>
      <Button
        colorScheme='blue'
        flexShrink={0}
        height='40px'
        isLoading={isSubmitting}
        px='20px'
        type='submit'
        variant='solid'
      >
        {COPY.stayPosted.submit}
      </Button>
    </Flex>
  );
}
